"use client"

import { Link } from "react-router-dom"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from "@/components/forms/form/Form"
import FormWrap from "@/components/forms/form/FormWrap"
import { Input } from "@/components/forms/inputs/text/Input"
import { useState } from "react"
import { forgotPasswordService } from "@/api/services/auth/authService"
import displayError from "@/api/errors/errorResponse"
import { Alert } from "@/components/alert"
import skillpassLogo from "@/assets/skillpass_v2.svg"

const forgotPasswordSchema = z.object({
  email: z.string().email("Wprowadź poprawny adres email")
})

type ForgotPasswordData = z.infer<typeof forgotPasswordSchema>

export default function ForgotPassword() {
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const form = useForm<ForgotPasswordData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: ""
    }
  })

  const onSubmit = async (data: ForgotPasswordData) => {
    setLoading(true)
    try {
      const res = await forgotPasswordService(data)
      if (!res.success) {
        displayError(res)
        setError("Wystąpił błąd podczas wysyłania linku do resetowania hasła")
        setSuccess(null)
        setLoading(false)
        return
      }
      setSuccess("Link do resetowania hasła został wysłany na podany adres email")
      setError(null)
    } catch (err) {
      setError("Wystąpił błąd podczas wysyłania linku do resetowania hasła")
      setSuccess(null)
    }
    setLoading(false)
  }

  return (
    <div className="min-h-screen flex">
      {/* Lewa strona z logo */}
      <div className="w-1/2 bg-white flex items-center justify-center p-8">
        <div className="w-full">
          <img src={skillpassLogo} alt="Skillpass Logo" className="w-full h-auto" />
        </div>
      </div>

      {/* Prawa strona z formularzem */}
      <div className="w-1/2 bg-gradient-to-br from-[#1d475f] to-[#2a5f7c] flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <h1 className="text-white text-2xl font-semibold mb-2">Resetowanie hasła</h1>
            <p className="text-gray-200 text-sm">Wprowadź swój adres email, aby zresetować hasło.</p>
          </div>

          {error && (
            <Alert variant="error" className="mb-4">
              {error}
            </Alert>
          )}

          {success && (
            <Alert variant="success" className="mb-4">
              {success}
            </Alert>
          )}

          {!success && (
            <Form {...form}>
              <FormWrap
                buttonClassName="w-full bg-white hover:bg-gray-100 text-[#1d475f] font-medium mt-6"
                onSubmit={form.handleSubmit(onSubmit)}
                submitText="Wyślij link do resetowania"
                disabled={loading}
              >
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-white">Email</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Wprowadź email"
                          type="email"
                          {...field}
                          required
                          className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                        />
                      </FormControl>
                      <FormMessage className="text-red-300" />
                    </FormItem>
                  )}
                />
              </FormWrap>
            </Form>
          )}

          <div className="mt-8 text-center">
            <Link to="/login" className="text-sm text-white hover:text-white/90 font-medium">
              Powróć do logowania
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
